import {
    ArrowDropDown,
    Check,
    Code,
    Description,
    ExitToApp,
    Expand,
    OpenInFull,
    TableChart,
} from '@mui/icons-material';
import {
    AppBar,
    Box,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
    allOptimizationObjectivesState,
    optimizationObjectivesState,
} from '~/state/atoms/dose/optimizationObjectivesAtom';
import { Features } from '~/components/FeatureToggle';
import useDose from '~/hooks/patient/dose/useDose';
import {
    expandDoseStatisticsTableState,
    showDoseStatisticsTableFullScreenState,
} from '~/state/atoms/viewerAtoms';
import AppConfig from '~/utils/appconfig';
import {
    createJSONContent,
    createCSVContent,
    csvEscapeCell,
    downloadCreatedFile,
} from '~/utils/io/files/handleFileExport';
import DoseStatisticsTable from './DoseStatisticsTable';
import OptimizationObjectivesComponent from './optimization-objectives';
import { currentDoseState } from '~/state/atoms/dose/doseAtoms';
import { getDICOMDate, getDICOMTime } from '~/domain/dicom/utils';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { LTTB } from 'downsample';
import useDvh from '~/hooks/patient/dose/useDvh';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { roisForDvhState } from '~/state/atoms/structureSet/roiAtoms';
import { CurveData } from '~/domain/patient/dvh';

const TablePanel: React.FC = () => {
    const { formatMessage: t } = useIntl();
    const isOptimizationObjectivesFeatureEnabled = AppConfig.isFeatureEnabled(
        Features.OPTIMIZATION_OBJECTIVES
    );

    const { dose } = useDose();
    const optimizationObjectives = useRecoilValue(optimizationObjectivesState);
    const allOptimizationObjectiveRows = useRecoilValue(
        allOptimizationObjectivesState
    );
    const { getDvhForRoi, getAllDvhs } = useDvh();
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const roisForDvh = useRecoilValue(roisForDvhState);
    const selectedPatient = useSelectedPatient();

    const [expandDoseStatisticsTable, setExpandDoseStatisticsTable] =
        useRecoilState(expandDoseStatisticsTableState);
    const [
        showDoseStatisticsTableFullScreen,
        setShowDoseStatisticsTableFullScreen,
    ] = useRecoilState(showDoseStatisticsTableFullScreenState);

    const currentDose = useRecoilValue(currentDoseState);

    const [visibleDvhs, setVisibleDvhs] = useState<CurveData[]>([]);
    const curvesRef = useRef<CurveData[]>([]);
    useEffect(() => {
        let cancelled = false;
        curvesRef.current = [];
        setVisibleDvhs([]);

        if (!dose || !roisForDvh.length) {
            return;
        }

        const computeOne = async (
            roiNumber: number
        ): Promise<CurveData | undefined> => {
            // Yield to let UI paint between items
            await new Promise((r) => requestAnimationFrame(r));
            if (cancelled) {
                return;
            }
            return getDvhForRoi(roiNumber);
        };

        (async () => {
            const tasks = roisForDvh.map(async (roi) => {
                const curve = await computeOne(roi.roiNumber);
                if (cancelled || !curve) {
                    return;
                }
                curvesRef.current.push(curve);
                setVisibleDvhs([...curvesRef.current]);
            });
            await Promise.all(tasks);
        })();

        return () => {
            cancelled = true;
        };
    }, [dose, roisForDvh, getDvhForRoi]);

    const [selectedComponent, setSelectedComponent] =
        useState<string>('doseStatistics');

    useEffect(() => {
        if (optimizationObjectives.length === 0) {
            setSelectedComponent((current) =>
                current === 'optimizationObjectives'
                    ? 'doseStatistics'
                    : current
            );
        }
    }, [optimizationObjectives]);

    const [exportBtnAnchorEl, setExportBtnAnchorEl] =
        useState<HTMLButtonElement | null>(null);

    const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setExportBtnAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setExportBtnAnchorEl(null);
    };
    const getSelectedPatientDetails = () => {
        if (
            !selectedPatient?.dataSet ||
            !currentDose ||
            !currentDose.structureSet
        ) {
            return {
                patientId: '',
                doseUid: '',
                structureSetLabel: '',
                imageSeriesDescription: '',
            };
        }

        return {
            patientId: selectedPatient.dataSet.PatientID,
            doseUid: currentDose.dicomUid,
            structureSetLabel: currentDose.structureSet.structureSetLabel,
            imageSeriesDescription:
                currentDose.structureSet.image?.seriesMetadata
                    .seriesDescription ?? '',
        };
    };

    const handleExportCsv = () => {
        const currentDate = getDICOMDate(new Date());
        const currentTime = getDICOMTime();
        const {
            patientId,
            doseUid,
            structureSetLabel,
            imageSeriesDescription,
        } = getSelectedPatientDetails();

        const summaryHeaderRow = [
            'PatientID',
            'StructureSetLabel',
            'DoseUID',
            'ImageSeriesDescription',
        ];
        const summaryDataRows = [
            [patientId, structureSetLabel, doseUid, imageSeriesDescription].map(
                csvEscapeCell
            ),
        ];
        const summarySection = [summaryHeaderRow, ...summaryDataRows]
            .map((row) => row.join(','))
            .join('\n');

        if (selectedComponent === 'doseStatistics') {
            const doseHeaderRow = [
                'Structure',
                'Min Dose (Gy)',
                'Max Dose (Gy)',
                'Mean Dose (Gy)',
            ];
            const doseDataRows = [...visibleDvhs]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((dvh) => [
                    csvEscapeCell(dvh.name),
                    dvh.doseMinGy.toFixed(3),
                    dvh.doseMaxGy.toFixed(3),
                    dvh.doseMeanGy.toFixed(3),
                ]);

            const doseSection = [doseHeaderRow, ...doseDataRows]
                .map((row) => row.join(','))
                .join('\n');

            const csvUri = createCSVContent('', [
                `${summarySection}\n\n${doseSection}`,
            ]);
            downloadCreatedFile(
                csvUri,
                `dose_statistics_${currentDate}_${currentTime}.csv`
            );
        } else if (selectedComponent === 'optimizationObjectives') {
            const headerRow = [
                'Structure',
                'Type',
                'Volume (%)',
                'gEUD a',
                'Dose (Gy)',
                'Weight',
            ];
            const sortedRows = [...allOptimizationObjectiveRows].sort((a, b) =>
                a.structure.localeCompare(b.structure)
            );
            const dataRows = sortedRows.map((row) => [
                csvEscapeCell(row.structure),
                csvEscapeCell(row.type),
                row.volumePercent,
                row.gEUD,
                row.dose,
                row.priority,
            ]);

            const objectivesSection = [headerRow, ...dataRows]
                .map((row) => row.join(','))
                .join('\n');

            const csvUri = createCSVContent('', [
                `${summarySection}\n\n${objectivesSection}`,
            ]);
            downloadCreatedFile(
                csvUri,
                `optimization_objectives_${currentDate}_${currentTime}.csv`
            );
        }

        handleMenuClose();
    };

    const handleExportJson = () => {
        const currentDate = getDICOMDate(new Date());
        const currentTime = getDICOMTime();

        if (selectedComponent === 'doseStatistics') {
            const jsonData = [...visibleDvhs]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((dvh) => ({
                    structure: dvh.name,
                    doseMinGy: dvh.doseMinGy.toFixed(3),
                    doseMaxGy: dvh.doseMaxGy.toFixed(3),
                    doseMeanGy: dvh.doseMeanGy.toFixed(3),
                }));

            const jsonUri = createJSONContent(jsonData);
            downloadCreatedFile(
                jsonUri,
                `dose_statistics_${currentDate}_${currentTime}.json`
            );
        } else if (selectedComponent === 'optimizationObjectives') {
            const jsonData = [...allOptimizationObjectiveRows]
                .sort((a, b) => a.structure.localeCompare(b.structure))
                .map((row) => ({
                    structure: row.structure,
                    type: row.type,
                    volumePercent: row.volumePercent,
                    doseGy: row.dose,
                    priority: row.priority,
                    gEUDa: row.gEUD,
                }));

            const jsonUri = createJSONContent(jsonData);
            downloadCreatedFile(
                jsonUri,
                `optimization_objectives_${currentDate}_${currentTime}.json`
            );
        }

        handleMenuClose();
    };

    const handleExportDvhCsv = async () => {
        const roiDvhs = await getAllDvhs();
        if (!roiDvhs || roiDvhs.length === 0) {
            console.warn('No DVH data available to export.');
            return;
        }
        const currentDate = getDICOMDate(new Date());
        const currentTime = getDICOMTime();
        const globalMetadata = [
            `Patient Name: ${
                selectedPatient?.dataSet.PatientName || 'Unknown'
            }`,
            `Patient ID: ${selectedPatient?.dataSet.PatientID || 'Unknown'}`,
            `Plan: ${dose?.planLabel || 'Unknown'}`,
            `Comment: ${dose?.doseComment || 'Unknown'}`,
            `Export Date: ${currentDate}`,
            ``,
        ].join('\n');
        const doseHeader =
            'Dose [Gy], Relative Dose [%], Ratio of Total Structure Volume [%]';
        const doseDataRows = roiDvhs.map((roiDvh) => {
            const structure = currentStructureSet?.roiArray.find(
                (roi) => roi.roiNumber === roiDvh.roiNumber
            );
            const roiVolume = roiDvh.dvh.structureVolumeCm3 || 0;
            const roiMetadata = [
                `Structure: ${roiDvh.name}`,
                `Approval Status: ${structure?.approvalStatus || 'Unknown'}`,
                `Volume [cm³]: ${roiVolume.toFixed(3)}`,
                `Min Dose [Gy]: ${roiDvh.dvh.doseMinGy.toFixed(3)}`,
                `Max Dose [Gy]: ${roiDvh.dvh.doseMaxGy.toFixed(3)}`,
                `Mean Dose [Gy]: ${roiDvh.dvh.doseMeanGy.toFixed(3)}`,
                `Equiv. Sphere Diam. [cm]: ${(
                    2 * Math.cbrt((3 * roiVolume) / (4 * Math.PI))
                ).toFixed(3)}`,
                ``,
            ].join('\n');
            const originalData = Array.from(roiDvh.dvh.dvhData, (y, x) => ({
                x: x * roiDvh.dvh.binWidth,
                y,
            }));
            const downsampledData = LTTB(originalData, 1000) as {
                x: number;
                y: number;
            }[];
            const doseRows: string[] = [];
            downsampledData.forEach((point) => {
                if (point.x >= 0 && dose?.normalizationValueGy) {
                    const relativeDose =
                        (100 * point.x) / dose?.normalizationValueGy;
                    doseRows.push(
                        `${point.x.toFixed(3)}, ${relativeDose.toFixed(
                            3
                        )}, ${point.y.toFixed(3)}`
                    );
                }
            });
            return `${roiMetadata}\n${doseHeader}\n${doseRows.join('\n')}`;
        });
        const csvContent = `${globalMetadata}\n${doseDataRows.join('\n\n')}`;
        const csvUri = createCSVContent('', [csvContent]);
        downloadCreatedFile(
            csvUri,
            `dvh_export_${currentDate}_${currentTime}.csv`
        );
        handleMenuClose();
    };

    const renderComponent = () => {
        switch (selectedComponent) {
            case 'doseStatistics':
                return <DoseStatisticsTable dvhCurves={visibleDvhs} />;
            case 'optimizationObjectives':
                return <OptimizationObjectivesComponent />;
            default:
                return null;
        }
    };

    const handleTabChange = (
        _event: React.SyntheticEvent,
        newValue: string
    ) => {
        setSelectedComponent(newValue);
    };

    const renderButtons = () => (
        <React.Fragment>
            <Tooltip title={t({ id: 'export' })} placement="top">
                <IconButton
                    color="secondary"
                    onClick={handleExportClick}
                    aria-label={t({ id: 'export' })}
                >
                    <ExitToApp />
                    <ArrowDropDown sx={{ fontSize: 16 }} />
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={exportBtnAnchorEl}
                open={Boolean(exportBtnAnchorEl)}
                onClose={handleMenuClose}
                sx={{ zIndex: (theme) => theme.zIndex.modal + 200 }}
                anchorOrigin={{
                    vertical: expandDoseStatisticsTable ? 'top' : 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: expandDoseStatisticsTable ? 'bottom' : 'top',
                    horizontal: 'left',
                }}
            >
                {selectedComponent === 'doseStatistics' && (
                    <MenuItem onClick={handleExportDvhCsv} sx={{ py: 1 }}>
                        <Description
                            fontSize="small"
                            style={{ marginRight: 8 }}
                        />
                        <Typography fontSize={13}>
                            {t({ id: 'exportDvhAsCsv' })}
                        </Typography>
                    </MenuItem>
                )}
                {selectedComponent === 'doseStatistics' && <Divider />}
                <MenuItem onClick={handleExportCsv} sx={{ py: 1 }}>
                    <Description fontSize="small" style={{ marginRight: 8 }} />
                    <Typography fontSize={13}>
                        {t({
                            id:
                                selectedComponent === 'optimizationObjectives'
                                    ? 'exportOptimizationObjectivesAsCsv'
                                    : 'exportDoseStatisticsAsCsv',
                        })}
                    </Typography>
                </MenuItem>
                <MenuItem onClick={handleExportJson} sx={{ py: 1 }}>
                    <Code fontSize="small" style={{ marginRight: 8 }} />
                    <Typography fontSize={13}>
                        {t({
                            id:
                                selectedComponent === 'optimizationObjectives'
                                    ? 'exportOptimizationObjectivesAsJson'
                                    : 'exportDoseStatisticsAsJson',
                        })}
                    </Typography>
                </MenuItem>
            </Menu>

            <Tooltip title={t({ id: 'resize' })} placement="top">
                <IconButton
                    color="primary"
                    onClick={() =>
                        showDoseStatisticsTableFullScreen
                            ? setShowDoseStatisticsTableFullScreen(false)
                            : setExpandDoseStatisticsTable(
                                  !expandDoseStatisticsTable
                              )
                    }
                    aria-label={t({ id: 'resize' })}
                >
                    <Expand />
                </IconButton>
            </Tooltip>

            <Tooltip title={t({ id: 'maximize' })} placement="top">
                <IconButton
                    color="primary"
                    onClick={() => {
                        if (expandDoseStatisticsTable) {
                            setExpandDoseStatisticsTable(false);
                            setShowDoseStatisticsTableFullScreen(true);
                        } else {
                            setShowDoseStatisticsTableFullScreen(
                                !showDoseStatisticsTableFullScreen
                            );
                        }
                    }}
                    aria-label={t({ id: 'maximize' })}
                >
                    <OpenInFull />
                </IconButton>
            </Tooltip>
        </React.Fragment>
    );

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <AppBar position="static" sx={{ height: '55px' }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '100%',
                    }}
                >
                    <Tabs
                        value={selectedComponent}
                        onChange={handleTabChange}
                        indicatorColor="primary"
                        textColor="inherit"
                        sx={{ height: '100%' }}
                    >
                        <Tab
                            label="Dose Statistics"
                            value="doseStatistics"
                            icon={<TableChart />}
                            iconPosition="start"
                            sx={{ height: '55px', minHeight: '55px' }}
                        />
                        {isOptimizationObjectivesFeatureEnabled && (
                            <Tab
                                label={
                                    <Tooltip
                                        title={
                                            optimizationObjectives.length === 0
                                                ? t({
                                                      id: 'optimizationObjectivesNotAvailable',
                                                  })
                                                : undefined
                                        }
                                        placement="bottom"
                                        disableHoverListener={
                                            optimizationObjectives.length > 0
                                        }
                                    >
                                        <span style={{ pointerEvents: 'auto' }}>
                                            {'Optimization Objectives'}
                                        </span>
                                    </Tooltip>
                                }
                                value="optimizationObjectives"
                                icon={<Check />}
                                iconPosition="start"
                                sx={{ height: '55px', minHeight: '55px' }}
                                disabled={optimizationObjectives.length === 0}
                            />
                        )}
                    </Tabs>
                    <Box sx={{ display: 'flex', gap: 1, pr: 2 }}>
                        {renderButtons()}
                    </Box>
                </Box>
            </AppBar>
            {showDoseStatisticsTableFullScreen ? (
                <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
                    <Box sx={{ position: 'absolute', inset: 0 }}>
                        {renderComponent()}
                    </Box>
                </Box>
            ) : (
                <Box
                    sx={{
                        width: '100%',
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                    }}
                >
                    {renderComponent()}
                </Box>
            )}
        </Box>
    );
};

export default TablePanel;
